import { useQuery } from '@tanstack/react-query';
import {
  AppWindow,
  Copy,
  ExternalLink,
  LayoutPanelTop,
  MessageCircle,
  Monitor,
  Smartphone,
} from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import type { Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { PreviewContext } from '@/Domain/components/PreviewProvider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { TooltipProvider } from '@/Domain/components/Shared/Tooltip/Tooltip';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';
import { BrowserHeaderButton } from '@/UI/ChatPage/SafariBrowserFrame';
import { AppearanceLoader } from '@/UI/Website/Appearance/Appearance.loader';
import { Install } from '@/UI/Website/Install/Install';
import { TextContentLoader } from '@/UI/Website/TextContent/TextContent.loader';
import { WidgetPreview } from '@/UI/Website/WidgetPreview';
import { WEBSITE_VIEW_QUERY_PARAM, WebsiteView, isValidWebsiteView } from '@/UI/Website/viewMode';
import { CHAT_PAGE_ACCESS_TOKEN_KEY, getPreviewIdentityToken } from '@/context';

export interface ImplementationData {
  scenario_id: string;
  quickchatjs_url: string;
  api_server_url: string;
  chat_avatar: string;
  public_url_active: boolean;
  has_shopify_mcp_action: boolean;
}

enum TabsEnum {
  Install = 'install',
  Appearance = 'appearance',
  TextContent = 'text_content',
}

type DeviceMode = 'mobile' | 'desktop';
type ColorSchemeMode = 'light' | 'dark';

const FAKE_URL = 'yourwebsite.com';

const VALID_TABS = new Set<string>(Object.values(TabsEnum));

export const WidgetSettingsPage = () => {
  const { t } = useTranslation('website');
  useTrackPageVisit('your_website');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    initialTab && VALID_TABS.has(initialTab) ? initialTab : TabsEnum.Install,
  );
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const initialView = searchParams.get(WEBSITE_VIEW_QUERY_PARAM);
  const [viewMode, setViewMode] = useState<WebsiteView>(
    isValidWebsiteView(initialView) ? initialView : WebsiteView.Bubble,
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === TabsEnum.Install) {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', value);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const integrationsData = useQuery<Integrations>({ queryKey: ['integrations'] });

  const widgetSettings = useQuery<WidgetSettingsDto>({ queryKey: ['widget-settings'] });
  const widgetThemeMode = widgetSettings.data?.theme ?? 'light';
  const effectiveColorScheme: ColorSchemeMode = widgetThemeMode === 'dark' ? 'dark' : 'light';

  const publicChatPageUrl = window.location.origin + (integrationsData?.data?.links.public ?? '');

  const handleOpenPreview = () => {
    let link: string | undefined;
    if (viewMode === WebsiteView.Bubble) link = integrationsData?.data?.links.widget;
    else if (viewMode === WebsiteView.Embed) link = integrationsData?.data?.links.embed;
    else link = integrationsData?.data?.links.public;
    if (link) window.open(window.location.origin + link);
  };

  const handleCopyChatPageLink = () => {
    copyToClipboard(
      publicChatPageUrl,
      () => toast.success(t('public_link.toast.copy.success')),
      () => toast.error(t('public_link.toast.copy.fail')),
    );
  };

  const previewContext = useContext(PreviewContext);
  const prevBubbleReqRef = useRef(0);
  useEffect(() => {
    const reqId = previewContext?.bubbleViewRequestId ?? 0;
    if (reqId > prevBubbleReqRef.current) {
      prevBubbleReqRef.current = reqId;
      setViewMode(WebsiteView.Bubble);
    }
  }, [previewContext?.bubbleViewRequestId]);

  // Track tab-label visibility so the Widget/Embed and Desktop/Mobile tab
  // tooltips only appear when their text is gone. The tabs collapse at `@md`
  // (28rem) container query; we observe the actual computed `display` of the
  // sentinel span so JS stays in lockstep with the CSS. The BrowserHeaderButton
  // (Open in new tab) manages its own tooltip gating internally — it lives under
  // a different @container (the SafariBrowserFrame) so per-button detection is
  // required to handle the mobile-preview narrow frame correctly.
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const tabLabelSentinelRef = useRef<HTMLSpanElement>(null);
  const [showLabels, setShowLabels] = useState(true);
  useEffect(() => {
    const container = previewSectionRef.current;
    const tabSentinel = tabLabelSentinelRef.current;
    if (!container || !tabSentinel) return;
    const update = () => setShowLabels(getComputedStyle(tabSentinel).display !== 'none');
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const previewIdentityToken = getPreviewIdentityToken();
  const customParams = previewIdentityToken
    ? { [CHAT_PAGE_ACCESS_TOKEN_KEY]: previewIdentityToken }
    : undefined;

  const newTabButton = (
    <BrowserHeaderButton
      icon={ExternalLink}
      label={t('preview_link.label')}
      onClick={handleOpenPreview}
      aria-label={t('preview_link.tooltip')}
      data-testid="open-preview-button"
      tooltip={
        <>
          <p>{t('preview_link.tooltip')}</p>
          <p className="text-muted-foreground">{t('preview_link.access_info')}</p>
        </>
      }
    />
  );

  const copyLinkButton = (
    <BrowserHeaderButton
      icon={Copy}
      label={t('public_link.button.copy_link')}
      onClick={handleCopyChatPageLink}
      aria-label={t('public_link.button.copy_link')}
      data-testid="copy-link-button"
      tooltip={t('public_link.button.copy_link')}
    />
  );

  const isChatPageView = viewMode === WebsiteView.ChatPage;
  const previewUrl = isChatPageView ? publicChatPageUrl : FAKE_URL;
  const previewUrlAction = isChatPageView ? copyLinkButton : undefined;

  return (
    <div className="-m-6 flex h-[calc(100vh-var(--header-height,49px))] overflow-hidden">
      <div
        className="w-full sm:max-w-md sm:shrink-0 overflow-auto bg-card p-6 custom-scroll"
        style={{ scrollbarGutter: 'stable' }}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2 items-baseline mb-6">
            <h1 className="font-semibold text-lg">{t('title')}</h1>
          </div>

          {/* Mobile preview links live inside the Install tab now (per
              section: Widget / Embed / Chat Page). The top-of-page block was
              removed to avoid duplication. */}

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value={TabsEnum.Install}>{t('tabs.install')}</TabsTrigger>
              <TabsTrigger value={TabsEnum.Appearance}>{t('tabs.appearance')}</TabsTrigger>
              <TabsTrigger value={TabsEnum.TextContent}>{t('tabs.text_content')}</TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <TabsContent
                value={TabsEnum.Install}
                forceMount
                className="data-[state=inactive]:hidden"
              >
                <Install />
              </TabsContent>
              <TabsContent
                value={TabsEnum.Appearance}
                forceMount
                className="data-[state=inactive]:hidden"
              >
                <AppearanceLoader />
              </TabsContent>
              <TabsContent
                value={TabsEnum.TextContent}
                forceMount
                className="data-[state=inactive]:hidden"
              >
                <TextContentLoader />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      <div className="hidden sm:block w-px bg-border shrink-0" />

      <div
        ref={previewSectionRef}
        className="@container hidden sm:flex flex-1 min-w-0 overflow-hidden bg-muted flex-col p-5"
      >
        <TooltipProvider>
          <div className="flex items-center gap-2 shrink-0 mb-4">
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as WebsiteView)}>
              <TabsList className="border border-border">
                <TabsTrigger
                  value={WebsiteView.Bubble}
                  icon={MessageCircle}
                  aria-label={t('preview_controls.widget_aria')}
                  tooltip={showLabels ? undefined : t('preview_controls.widget')}
                  className="h-7 @max-md:gap-0"
                >
                  <span ref={tabLabelSentinelRef} className="hidden @md:inline">
                    {t('preview_controls.widget')}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value={WebsiteView.Embed}
                  icon={AppWindow}
                  aria-label={t('preview_controls.embed_aria')}
                  tooltip={showLabels ? undefined : t('preview_controls.embed')}
                  className="h-7 @max-md:gap-0"
                >
                  <span className="hidden @md:inline">{t('preview_controls.embed')}</span>
                </TabsTrigger>
                <TabsTrigger
                  value={WebsiteView.ChatPage}
                  icon={LayoutPanelTop}
                  aria-label={t('preview_controls.chat_page_aria')}
                  tooltip={showLabels ? undefined : t('preview_controls.chat_page')}
                  className="h-7 @max-md:gap-0"
                >
                  <span className="hidden @md:inline">{t('preview_controls.chat_page')}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Tabs value={deviceMode} onValueChange={(v) => setDeviceMode(v as DeviceMode)}>
              <TabsList className="border border-border">
                <TabsTrigger
                  value="desktop"
                  icon={Monitor}
                  aria-label={t('preview_controls.desktop_aria')}
                  tooltip={showLabels ? undefined : t('preview_controls.desktop')}
                  className="h-7 @max-md:gap-0"
                >
                  <span className="hidden @md:inline">{t('preview_controls.desktop')}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="mobile"
                  icon={Smartphone}
                  aria-label={t('preview_controls.mobile_aria')}
                  tooltip={showLabels ? undefined : t('preview_controls.mobile')}
                  className="h-7 @max-md:gap-0"
                >
                  <span className="hidden @md:inline">{t('preview_controls.mobile')}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div data-testid="preview-color-toggle-locked" />
          </div>
        </TooltipProvider>

        <div className="flex-1 flex items-center justify-center w-full overflow-hidden">
          <div
            className={
              deviceMode === 'mobile' ? 'h-full aspect-[390/844] max-w-full' : 'w-full h-full'
            }
          >
            <WidgetPreview
              viewMode={viewMode}
              deviceMode={deviceMode}
              url={previewUrl}
              urlAction={previewUrlAction}
              customParams={customParams}
              headerAction={newTabButton}
              colorScheme={effectiveColorScheme}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
